import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import { TAU, buildRatioSummary, buildSharesSummary, fraction, isFiniteNumber, ringSegments } from '@vello/charts-core';
import { ChartEmpty, ChartLive, ChartLoading, ChartTooltip, cascade, colorOf, cx, defaultFormatValue, joinSummary, toneColor, useElementWidth, useMotionState, useTweenedValues, type ChartToneName } from './kit';
import { CountUp } from './count-up';
import type { ChartBaseProps } from './line-chart';
import type { ChartSelect } from './bars';

// Rings are SVG circles drawn with a dashed stroke over a path normalised to 100 units (`pathLength`), so a slice is just
// `length 100` with an offset — one geometry for the sweep entrance and for the old → new tween. The charts-core
// `ringSegments` supplies the angles (and `arcPath` / `strokeArcPath` the shapes for a renderer that needs real paths, e.g. native).

const VIEW = 100;
const CENTER = VIEW / 2;
/** How many viewBox units the stroke of a hovered, focused or chosen slice grows by (D566). */
const SLICE_GROW = 3;

function pct(f: number): string {
  return `${Math.round(f * 100)}%`;
}

export interface DonutSlice {
  readonly key?: string;
  readonly label: string;
  readonly value: number;
  readonly tone?: ChartToneName;
}

export interface DonutChartProps extends ChartBaseProps {
  readonly slices: readonly DonutSlice[];
  /** The largest rendered diameter, in CSS px (default 160). The ring shrinks to fit a narrower container. */
  readonly size?: number;
  /** Ring thickness as a percentage of the radius, 8–40 (default 22). */
  readonly thickness?: number;
  /** Content in the hole, e.g. a `CountUp` of the total and its label. */
  readonly center?: ReactNode;
  /** Format a share (a fraction 0–1) in the legend (default: a whole percent). */
  readonly formatShare?: (fraction: number) => string;
  /** The legend with label, value and share for every slice (default true — it is also what makes colour not the only signal). */
  readonly showLegend?: boolean;
  /**
   * D528: make each slice (and its legend row) clickable. A click on a slice or on its legend button - which is how a keyboard reaches it -
   * calls this with the slice and whether Shift, Ctrl or Cmd was held.
   */
  readonly onSelect?: (slice: DonutSlice, index: number, how: ChartSelect) => void;
  /** Keys of the slices to mark as selected (`aria-pressed` on the legend button), when `onSelect` is given. */
  readonly selectedKeys?: readonly string[];
}

/** A donut of the parts of a whole, with a legend that names every slice with its value and share. */
export function DonutChart(props: DonutChartProps): ReactElement {
  const size = props.size ?? 160;
  if (props.loading === true) return <ChartLoading height={size} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  const total = props.slices.reduce((sum, s) => sum + (isFiniteNumber(s.value) && s.value > 0 ? s.value : 0), 0);
  if (total <= 0) return <ChartEmpty height={size} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <DonutBody {...props} size={size} />;
}

function DonutBody(props: DonutChartProps & { size: number }): ReactElement {
  const { slices, size } = props;
  const format = props.formatValue ?? defaultFormatValue;
  const formatShare = props.formatShare ?? pct;
  const motion = useMotionState(props.animate);
  const target = slices.map((s) => (isFiniteNumber(s.value) && s.value > 0 ? s.value : 0));
  const drawn = useTweenedValues(target, motion).map((v) => v ?? 0);
  const segments = ringSegments(drawn, { gap: drawn.filter((v) => v > 0).length > 1 ? 0.04 : 0 });
  const thickness = Math.min(40, Math.max(8, props.thickness ?? 22)) / 100;
  const radius = CENTER * (1 - thickness / 2) - 1;
  const stroke = CENTER * thickness;
  const summary = props.ariaLabel ?? joinSummary([props.title, buildSharesSummary(slices.map((s) => ({ label: s.label, value: s.value })), { format, formatShare })]);
  const total = target.reduce((a, b) => a + b, 0);

  // D566: hover on a slice or on its legend row, or keyboard focus on a legend button, makes that slice active (grows, the others recede,
  // a tooltip with label / value / share). `selectedKeys` marks the chosen slices and rows with `data-chosen`.
  const figureRef = useRef<HTMLDivElement>(null);
  const figureWidth = useElementWidth(figureRef, size);
  const [activeIndex, setActive] = useState<number | null>(null);
  const keyOf = (i: number): string => slices[i]?.key ?? slices[i]?.label ?? '';
  const chosenKeys = props.selectedKeys ?? [];
  const anyChosen = chosenKeys.length > 0;
  const chosenOf = (i: number): { 'data-chosen'?: 'true' | 'false' } => (anyChosen ? { 'data-chosen': chosenKeys.includes(keyOf(i)) ? 'true' : 'false' } : {});
  const activeAttr = (i: number): { 'data-active'?: 'true' | 'false' } => (activeIndex === null ? {} : { 'data-active': activeIndex === i ? 'true' : 'false' });
  const activeSlice = activeIndex === null ? undefined : slices[activeIndex];
  const activeSeg = activeIndex === null ? undefined : segments[activeIndex];
  const activeShare = activeSlice !== undefined && total > 0 ? formatShare(fraction(Math.max(0, activeSlice.value), total)) : '';
  const activeValue = activeSlice !== undefined ? format(isFiniteNumber(activeSlice.value) ? activeSlice.value : 0) : '';
  const tipAt = ((): { x: number; y: number } | null => {
    if (activeSeg === undefined || activeSeg.fraction <= 0) return null;
    const mid = (activeSeg.startAngle + activeSeg.endAngle) / 2; // clockwise from 12 o'clock
    const scale = figureWidth / VIEW;
    return { x: (CENTER + (radius + stroke / 2) * Math.sin(mid)) * scale, y: (CENTER - (radius + stroke / 2) * Math.cos(mid)) * scale };
  })();

  return (
    <div className={cx('vello-chart', 'vello-donut', props.className)} data-motion={motion}>
      <div ref={figureRef} className="vello-donut__figure" style={{ maxInlineSize: size, inlineSize: '100%' }}>
        <svg className="vello-chart__svg vello-donut__svg" viewBox={`0 0 ${VIEW} ${VIEW}`} role="img" aria-label={summary}>
          <circle className="vello-chart__track" cx={CENTER} cy={CENTER} r={radius} fill="none" strokeWidth={stroke} />
          <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
            {segments.map((seg, i) => {
              if (seg.fraction <= 0) return null;
              const start = (seg.startAngle / TAU) * 100;
              const length = ((seg.endAngle - seg.startAngle) / TAU) * 100;
              const s = slices[i];
              const grown = activeIndex === i || chosenKeys.includes(keyOf(i));
              return (
                <circle
                  key={s?.key ?? `${s?.label}-${i}`}
                  className="vello-chart__slice vello-chart__sweep"
                  cx={CENTER}
                  cy={CENTER}
                  r={radius}
                  fill="none"
                  pathLength={100}
                  strokeWidth={grown ? stroke + SLICE_GROW : stroke}
                  stroke={colorOf(i, s?.tone)}
                  strokeDasharray={`${length.toFixed(3)} 100`}
                  strokeDashoffset={-start}
                  {...activeAttr(i)}
                  {...chosenOf(i)}
                  {...(props.onSelect !== undefined ? { 'data-selectable': 'true' } : {})}
                  onPointerEnter={() => setActive(i)}
                  onPointerLeave={() => setActive((prev) => (prev === i ? null : prev))}
                  style={cascade(i)}
                  {...(props.onSelect !== undefined && s !== undefined
                    ? { onClick: (e: React.MouseEvent) => props.onSelect?.(s, i, { additive: e.shiftKey || e.ctrlKey || e.metaKey }), 'aria-hidden': true }
                    : {})}
                />
              );
            })}
          </g>
        </svg>
        {props.center !== undefined && <div className="vello-donut__center">{props.center}</div>}
        {activeSlice !== undefined && tipAt !== null && (
          <ChartTooltip
            x={tipAt.x}
            y={tipAt.y}
            width={figureWidth}
            heading={activeSlice.label}
            rows={[
              { key: 'value', value: activeValue, color: colorOf(activeIndex ?? 0, activeSlice.tone) },
              ...(activeShare !== '' ? [{ key: 'share', value: activeShare }] : []),
            ]}
          />
        )}
      </div>
      <ChartLive text={activeSlice === undefined ? '' : `${activeSlice.label}: ${activeValue}${activeShare !== '' ? `, ${activeShare}` : ''}`} />
      {(props.showLegend ?? true) && (
        <ul className="vello-donut__legend">
          {slices.map((s, i) => {
            const body = (
              <>
                <span className="vello-chart__swatch" style={{ background: colorOf(i, s.tone) }} aria-hidden="true" />
                <span className="vello-donut__legend-label">{s.label}</span>
                <span className="tnum vello-donut__legend-value">{format(isFiniteNumber(s.value) ? s.value : 0)}</span>
                <span className="tnum vello-donut__legend-share">{total > 0 ? formatShare(fraction(Math.max(0, s.value), total)) : ''}</span>
              </>
            );
            const select = props.onSelect;
            return (
              <li
                key={s.key ?? `${s.label}-${i}`}
                className="vello-donut__legend-item"
                {...activeAttr(i)}
                {...chosenOf(i)}
                onPointerEnter={() => setActive(i)}
                onPointerLeave={() => setActive((prev) => (prev === i ? null : prev))}
                onFocus={() => setActive(i)}
                onBlur={() => setActive((prev) => (prev === i ? null : prev))}
              >
                {select === undefined ? (
                  body
                ) : (
                  <button
                    type="button"
                    className="vello-donut__legend-hit"
                    aria-pressed={chosenKeys.includes(keyOf(i))}
                    onClick={(e) => select(s, i, { additive: e.shiftKey || e.ctrlKey || e.metaKey })}
                  >
                    {body}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export interface RingStatProps extends ChartBaseProps {
  /** The figure (e.g. completed rides). */
  readonly value: number;
  /** The whole it is measured against (default 100). */
  readonly max?: number;
  /** A visible caption under the ring (localized). Also the first words of the accessible label. */
  readonly label?: string;
  readonly tone?: ChartToneName;
  /** The largest rendered diameter, in CSS px (default 112). The ring shrinks to fit a narrower container. */
  readonly size?: number;
  /** Ring thickness as a percentage of the radius, 8–40 (default 16). */
  readonly thickness?: number;
  /** Format the centred number from the percentage 0–100 (default `N%`). Ignored when `center` is set. */
  readonly formatCenter?: (percent: number) => string;
  /** Replace the centred number. */
  readonly center?: ReactNode;
}

/** A single value on a ring with the number centred in it (completion rate, utilisation). The number counts up with the sweep. */
export function RingStat(props: RingStatProps): ReactElement {
  const size = props.size ?? 112;
  if (props.loading === true) return <ChartLoading height={size} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  if (!isFiniteNumber(props.value)) return <ChartEmpty height={size} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <RingStatBody {...props} size={size} />;
}

function RingStatBody(props: RingStatProps & { size: number }): ReactElement {
  const format = props.formatValue ?? defaultFormatValue;
  const motion = useMotionState(props.animate);
  const max = props.max !== undefined && props.max > 0 ? props.max : 100;
  const target = fraction(Math.max(0, props.value), max);
  const drawn = useTweenedValues([target], motion)[0] ?? target;
  const thickness = Math.min(40, Math.max(8, props.thickness ?? 16)) / 100;
  const radius = CENTER * (1 - thickness / 2) - 1;
  const stroke = CENTER * thickness;
  const color = toneColor(props.tone ?? 'neutral');
  const percent = Math.round(target * 100);
  const formatCenter = props.formatCenter ?? ((n: number) => `${n}%`);
  const summary = props.ariaLabel ?? buildRatioSummary({ ...(props.label !== undefined ? { label: props.label } : {}), value: props.value, max, format });
  // D566: hovering the ring grows the arc and opens a tooltip with the label, the value against the whole and the percentage.
  const figureRef = useRef<HTMLDivElement>(null);
  const figureWidth = useElementWidth(figureRef, props.size);
  const [hover, setHover] = useState(false);

  return (
    <div className={cx('vello-chart', 'vello-ring', props.className)} data-motion={motion} data-tone={props.tone ?? 'neutral'}>
      <div ref={figureRef} className="vello-donut__figure" style={{ maxInlineSize: props.size, inlineSize: '100%' }} onPointerEnter={() => setHover(true)} onPointerLeave={() => setHover(false)}>
        <svg className="vello-chart__svg vello-donut__svg" viewBox={`0 0 ${VIEW} ${VIEW}`} role="img" aria-label={summary}>
          <circle className="vello-chart__track" cx={CENTER} cy={CENTER} r={radius} fill="none" strokeWidth={stroke} />
          <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
            {drawn > 0 && (
              <circle
                className="vello-chart__slice vello-chart__sweep vello-ring__arc"
                cx={CENTER}
                cy={CENTER}
                r={radius}
                fill="none"
                pathLength={100}
                strokeWidth={hover ? stroke + SLICE_GROW : stroke}
                data-active={hover ? 'true' : undefined}
                stroke={color}
                strokeLinecap="round"
                strokeDasharray={`${(drawn * 100).toFixed(3)} 100`}
              />
            )}
          </g>
        </svg>
        <div className="vello-donut__center vello-ring__center">{props.center ?? <CountUp value={percent} format={formatCenter} {...(props.animate !== undefined ? { animate: props.animate } : {})} />}</div>
        {hover && (
          <ChartTooltip
            x={figureWidth / 2}
            y={0}
            width={figureWidth}
            {...(props.label !== undefined ? { heading: props.label } : {})}
            rows={[
              { key: 'value', value: `${format(props.value)} / ${format(max)}`, color },
              { key: 'share', value: formatCenter(percent) },
            ]}
          />
        )}
      </div>
      {props.label !== undefined && <p className="vello-ring__label">{props.label}</p>}
    </div>
  );
}
